import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Share2, Sparkles, FileText, Image, File,
  Loader2, Clock, User, Tag, History, Activity, ChevronRight,
  Upload, Eye, Shield, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTranslation } from '../i18n';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/10 text-slate-600 border-slate-200' },
  uploaded: { label: 'Uploaded', color: 'bg-amber-500/10 text-amber-600 border-amber-200' },
  processed: { label: 'Analyzed', color: 'bg-green-500/10 text-green-600 border-green-200' },
  sent: { label: 'Sent', color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
  viewed: { label: 'Viewed', color: 'bg-purple-500/10 text-purple-600 border-purple-200' },
  signed: { label: 'Signed', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200' },
  archived: { label: 'Archived', color: 'bg-muted text-muted-foreground border-border' },
  error: { label: 'Error', color: 'bg-red-500/10 text-red-600 border-red-200' },
};

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

export default function DocumentViewerPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [analyzing, setAnalyzing] = useState(false);

  // Fetch document details
  const { data: doc, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: async () => {
      const docs = await api.get(`/documents?doc_id=${id}`);
      // The list endpoint returns an array, find our doc
      if (Array.isArray(docs)) return docs.find(d => d._id === id) || null;
      return docs;
    },
  });

  // Fetch presigned URL for preview
  const { data: previewUrl } = useQuery({
    queryKey: ['document-preview', id],
    queryFn: () => api.getDocumentPresignedUrl(id),
    enabled: !!doc,
  });

  // Fetch versions
  const { data: versions = [] } = useQuery({
    queryKey: ['document-versions', id],
    queryFn: () => api.get(`/documents/${id}/versions`),
  });

  // Fetch activity
  const { data: activity = [] } = useQuery({
    queryKey: ['document-activity', id],
    queryFn: () => api.get(`/documents/${id}/activity`),
  });

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/documents/${id}/analyze`);
      queryClient.invalidateQueries(['document', id]);
      toast.success('Analysis complete');
    } catch (err) {
      toast.error('Analysis failed');
    }
    setAnalyzing(false);
  };

  const handleDownload = async () => {
    try {
      const url = await api.getDocumentPresignedUrl(id);
      window.open(url, '_blank');
    } catch {
      toast.error('Download failed');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[500px] lg:col-span-2 rounded-xl" />
          <Skeleton className="h-[500px] rounded-xl" />
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="p-6 max-w-7xl mx-auto text-center py-20">
        <FileText className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
        <h2 className="text-lg font-semibold">{t("docViewer.notFound")}</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/documents')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Documents
        </Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.uploaded;
  const isImage = doc.mime_type?.startsWith('image/');
  const isPdf = doc.mime_type === 'application/pdf';
  const aiData = doc.ai_extracted_data;
  const tags = doc.tags || [];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-card flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => navigate('/documents')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold truncate">{doc.original_filename}</h1>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
              <Badge variant="outline" className={`text-[10px] ${statusConfig.color}`}>{statusConfig.label}</Badge>
              <span>{formatBytes(doc.size)}</span>
              <span>•</span>
              <span>{formatDate(doc.created_at)}</span>
              {doc.uploaded_by_name && (
                <>
                  <span>•</span>
                  <span>by {doc.uploaded_by_name}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handleAnalyze} disabled={analyzing}>
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {analyzing ? 'Analyzing...' : aiData ? 'Re-analyze' : 'Analyze with AI'}
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={handleDownload}>
            <Download className="h-4 w-4" /> Download
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Document Preview */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border bg-card overflow-hidden">
                {isPdf && previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title="Document Preview"
                    className="w-full bg-muted"
                    style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}
                  />
                ) : isImage && previewUrl ? (
                  <div className="flex items-center justify-center p-6 bg-muted/30" style={{ minHeight: '500px' }}>
                    <img
                      src={previewUrl}
                      alt={doc.original_filename}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-12 bg-muted/20" style={{ minHeight: '400px' }}>
                    <File className="h-16 w-16 text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground font-medium">{doc.mime_type || 'Unknown type'}</p>
                    <p className="text-sm text-muted-foreground/60 mt-1">{t("docViewer.previewUnavailable")}</p>
                    <Button variant="outline" className="mt-4 gap-2" onClick={handleDownload}>
                      <Download className="h-4 w-4" /> Download to view
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Details Panel */}
            <div className="space-y-4">
              {/* AI Analysis Card */}
              {aiData && (
                <div className="rounded-xl border bg-card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <h3 className="font-semibold text-sm">{t("docViewer.aiAnalysis")}</h3>
                    {aiData.confidence && (
                      <Badge variant="outline" className="ml-auto text-[10px]">
                        {Math.round(aiData.confidence * 100)}% confident
                      </Badge>
                    )}
                  </div>

                  {aiData.document_type && (
                    <div className="mb-3">
                      <span className="text-xs text-muted-foreground">{t("docViewer.documentType")}</span>
                      <p className="text-sm font-medium capitalize">{aiData.document_type}</p>
                    </div>
                  )}

                  {aiData.summary && (
                    <div className="mb-3">
                      <span className="text-xs text-muted-foreground">{t("docViewer.summary")}</span>
                      <p className="text-sm mt-1 text-foreground bg-muted/30 rounded-lg p-3 border">{aiData.summary}</p>
                    </div>
                  )}

                  {aiData.extracted_fields && Object.keys(aiData.extracted_fields).length > 0 && (
                    <div className="mb-3">
                      <span className="text-xs text-muted-foreground">{t("docViewer.extractedFields")}</span>
                      <div className="mt-1 space-y-1.5">
                        {Object.entries(aiData.extracted_fields).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-xs bg-muted/20 rounded px-2 py-1.5">
                            <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium text-foreground ml-2 text-right max-w-[60%] truncate">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiData.issues && aiData.issues.length > 0 && (
                    <div>
                      <span className="text-xs text-muted-foreground">{t("docViewer.issues")}</span>
                      <div className="mt-1 space-y-1">
                        {aiData.issues.map((issue, i) => (
                          <div key={i} className="flex gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/5 rounded p-2">
                            <Shield className="h-3 w-3 mt-0.5 shrink-0" />
                            <span>{issue}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <div className="rounded-xl border bg-card p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm">{t("docViewer.tags")}</h3>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabs: Versions & Activity */}
              <div className="rounded-xl border bg-card">
                <Tabs defaultValue="versions">
                  <TabsList className="w-full justify-start border-b rounded-none bg-transparent px-2 pt-2">
                    <TabsTrigger value="versions" className="gap-1.5 text-xs">
                      <History className="h-3.5 w-3.5" /> Versions ({versions.length})
                    </TabsTrigger>
                    <TabsTrigger value="activity" className="gap-1.5 text-xs">
                      <Activity className="h-3.5 w-3.5" /> Activity ({activity.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="versions" className="p-4">
                    {versions.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">{t("docViewer.noPreviousVersions")}</p>
                    ) : (
                      <div className="space-y-2">
                        {versions.map((v, i) => (
                          <div key={v._id || i} className="flex items-center justify-between text-xs border rounded-lg p-2.5">
                            <div>
                              <span className="font-medium">v{v.version_number || versions.length - i}</span>
                              <span className="text-muted-foreground ml-2">{formatDate(v.created_at)}</span>
                            </div>
                            <span className="text-muted-foreground">{formatBytes(v.size)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="activity" className="p-4">
                    {activity.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">{t("docViewer.noActivity")}</p>
                    ) : (
                      <div className="space-y-2">
                        {activity.slice(0, 20).map((a, i) => (
                          <div key={a._id || i} className="flex items-start gap-2 text-xs">
                            <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                              <User className="h-3 w-3 text-muted-foreground" />
                            </div>
                            <div>
                              <span className="font-medium">{a.user_name || 'System'}</span>
                              <span className="text-muted-foreground ml-1">{a.action}</span>
                              <p className="text-muted-foreground/60 mt-0.5">{formatDate(a.timestamp)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
